import React, { useEffect, useState } from "react";
import FinancialYearPage from "../../entities/FinancialYear";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import { useLocation } from "react-router-dom";
import {
  getCountry,
  persistSelectedCountry,
  resolveCountry,
} from "../../utils/country";

const FinancialYear = () => {
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [country, setCountry] = useState("");
  const location = useLocation();

  useEffect(() => {
    const resolvedCountry = resolveCountry(location?.state);
    setCountry(resolvedCountry);

    if (resolvedCountry) {
      persistSelectedCountry(resolvedCountry);
      getFinancialYears(resolvedCountry);
      return;
    }

    setFinancialYears([]);
    showAlert("Country is required to fetch financial years.", "error");
  }, [location?.state]);

  const requireCountry = (countryValue) => {
    const selectedCountry = getCountry(countryValue, country);
    if (!selectedCountry) {
      showAlert("Country is required for Financial Year actions.", "error");
      return "";
    }

    persistSelectedCountry(selectedCountry);
    setCountry(selectedCountry);
    return selectedCountry;
  };

  const getFinancialYears = async (countryValue = country) => {
    const selectedCountry = requireCountry(countryValue);
    if (!selectedCountry) return;

    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getFinancialYears(selectedCountry);
      if (result?.success) {
        setFinancialYears(result?.data || []);
        LoaderHelper.loaderStatus(false);
      } else {
        showAlert(result?.message || "Failed to fetch financial years", "error");
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred", "error");
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAddFinancialYear = async (data) => {
    const selectedCountry = requireCountry(data?.country);
    if (!selectedCountry) return;

    setLoading(true);
    try {
      const result = await AuthService.addFinancialYear({
        ...data,
        country: selectedCountry,
      });
      if (result?.success) {
        showAlert(result?.message || "Financial Year added successfully", "success");
        getFinancialYears(selectedCountry);
        setLoading(false);
      } else {
        showAlert(result?.message || "Failed to add financial year", "error");
        setLoading(false);
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred", "error");
      setLoading(false);
    }
  };

  const handleUpdateFinancialYear = async (id, data) => {
    const selectedCountry = requireCountry(data?.country);
    if (!selectedCountry) return;

    setLoading(true);
    try {
      const result = await AuthService.updateFinancialYear(id, {
        ...data,
        country: selectedCountry,
      });
      if (result?.success) {
        showAlert(result?.message || "Financial Year updated successfully", "success");
        getFinancialYears(selectedCountry);
        setLoading(false);
      } else {
        showAlert(result?.message || "Failed to update financial year", "error");
        setLoading(false);
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred", "error");
      setLoading(false);
    }
  };

  const handleDeleteFinancialYear = async (id) => {
    setLoading(true);
    try {
      const result = await AuthService.deleteFinancialYear(id);
      if (result?.success) {
        showAlert(result?.message || "Financial Year deleted successfully", "success");
        getFinancialYears();
        setLoading(false);
      } else {
        showAlert(result?.message || "Failed to delete financial year", "error");
        setLoading(false);
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred", "error");
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, isActive, item) => {
    const selectedCountry = requireCountry(item?.country);
    if (!selectedCountry) return;

    setLoading(true);
    try {
      const data = {
        start: item.start,
        end: item.end,
        isActive: isActive,
        country: selectedCountry,
      };
      const result = await AuthService.updateFinancialYear(id, data);
      if (result?.success) {
        showAlert(result?.message || "Status updated successfully", "success");
        getFinancialYears(selectedCountry);
        setLoading(false);
      } else {
        showAlert(result?.message || "Failed to update status", "error");
        setLoading(false);
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred", "error");
      setLoading(false);
    }
  };

  return (
    <FinancialYearPage
      country={country}
      financialYears={financialYears}
      loading={loading}
      handleAddFinancialYear={handleAddFinancialYear}
      handleUpdateFinancialYear={handleUpdateFinancialYear}
      handleDeleteFinancialYear={handleDeleteFinancialYear}
      handleToggleStatus={handleToggleStatus}
    />
  );
};

export default FinancialYear;
